import { useState, forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import Navbar from "@/components/Navbar";
import SEOHead from "@/components/SEOHead";
import heroBg from "@/assets/hero-bg.jpg";

const Login = forwardRef<HTMLDivElement>((_, ref) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error("Por favor, preencha todos os campos.");
      return;
    }
    setLoading(true);
    setTimeout(() => {
      toast.info("Funcionalidade de autenticação em desenvolvimento.");
      setLoading(false);
    }, 1000);
  };

  return (
    <div ref={ref}>
      <SEOHead
        title="Iniciar Sessão | Index Rerum"
        description="Aceda à plataforma Index Rerum para gerir o seu património e inventário museológico."
        canonicalPath="/login"
        noindex
      />
      <Navbar />
      <div className="min-h-screen flex pt-16">
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-end p-12">
          <img
            src={heroBg}
            alt="Interior de museu com peças de património cultural"
            className="absolute inset-0 w-full h-full object-cover"
            width={1920}
            height={1080}
          />
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative z-10 max-w-md">
            <p className="text-white/60 text-sm leading-relaxed font-light">
              Plataforma integrada de inventário e gestão de património — 100% web, segura e acessível em qualquer dispositivo.
            </p>
          </div>
        </div>

        <div className="w-full lg:w-1/2 bg-background flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-xs">
            <div className="mb-12">
              <h1 className="font-serif text-3xl font-bold text-foreground tracking-wider uppercase">Index Rerum</h1>
              <p className="text-muted-foreground text-xs mt-1 tracking-wide">by EVOKE IT</p>
            </div>

            <div className="mb-8">
              <h2 className="font-serif text-xl font-bold text-foreground italic">Acesso Reservado</h2>
              <p className="text-muted-foreground text-sm mt-1 font-light italic">Introduza as suas credenciais para aceder.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.2em] mb-2 block">
                  Utilizador
                </label>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-10 border-border border-t-0 border-x-0 rounded-none bg-transparent focus:border-foreground transition-colors px-0 text-sm"
                  placeholder="Utilizador"
                  maxLength={100}
                  autoComplete="username"
                />
              </div>

              <div>
                <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.2em] mb-2 block">
                  Senha
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-10 pr-10 border-border border-t-0 border-x-0 rounded-none bg-transparent focus:border-foreground transition-colors px-0 text-sm"
                    placeholder="Senha"
                    maxLength={128}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                variant="hero"
                size="lg"
                className="w-full h-11 text-sm tracking-wide mt-2"
                disabled={loading}
              >
                {loading ? "A entrar..." : "Entrar"}
              </Button>
            </form>

            <p className="text-center text-muted-foreground/60 text-xs mt-12">
              © 2026 Index Rerum by EVOKE IT
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

Login.displayName = "Login";

export default Login;
